import { Button } from './Button';
import { Dialog } from './Dialog';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What the person is agreeing to, and what it will cost them. */
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Uses the danger button, for something that cannot be undone. */
  destructive?: boolean;
  /** Disables both answers while the confirmed action is in flight. */
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A yes-or-no question asked before something hard to undo.
 *
 * Controlled by the caller, because a confirmation is a step in the middle of
 * something else: the save has already been asked for and is waiting on the
 * answer, so the thing that owns the save has to own whether the question is on
 * screen.
 *
 * Built on the shared `Dialog`, which brings the behaviour that is tedious to
 * get right: focus moves in on open and back to whatever opened it on close,
 * Tab is trapped inside, Escape closes it, and the page behind is hidden from
 * screen readers. All WCAG 2.1 AA requirements, and worth having in one place
 * rather than two.
 *
 * It asks for the narrow width and for no close X. An X on a question that is
 * waiting for an answer is a silent third answer: Escape and the backdrop both
 * mean "no" and say so through `onCancel`, but an X reads as "put this away",
 * which is not one of the two things on offer.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      // Escape and a click on the backdrop both mean "no". Closing a question
      // any other way would leave the caller waiting for an answer forever.
      onClose={onCancel}
      title={title}
      description={description}
      maxWidth="xs"
      dismissible={false}
      actions={
        <>
          {/* Cancel comes first, so opening the dialog puts focus on the answer
              that changes nothing. */}
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={pending}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
